/**
 * Admin Priority Management Helpers
 * Handles high priority assignment and management logic
 */

/**
 * Reset high priority for all admins except one
 * @param {Array<Object>} configs - Configuration array
 * @param {number} idToKeepPriority - Config id to set as high priority
 * @returns {Array<Object>} Updated configurations
 */
export const resetHighPriorityExcept = (configs, idToKeepPriority) => {
  return configs.map((config) => ({
    ...config,
    isHighPriority: config.id === idToKeepPriority,
  }));
};

/**
 * Ensures the only active admin always has high priority
 * This is a critical business rule: the single active admin must be high priority
 * 
 * @param {Array<Object>} configs - Admin configuration array
 * @param {boolean} roundRobinEnabled - Whether Round Robin mode is enabled
 * @param {Function} getActiveAdmins - Helper to get active admins
 * @returns {Array<Object>} Updated admin configurations
 */
export const ensureOnlyActiveAdminHasHighPriority = (configs, roundRobinEnabled, getActiveAdmins) => {
  // Skip if Round Robin is enabled (high priority is disabled in Round Robin mode)
  if (roundRobinEnabled) {
    return configs;
  }

  const activeAdmins = getActiveAdmins(configs);
  
  // If there's exactly one active admin, ensure they have high priority
  if (activeAdmins.length === 1) {
    return resetHighPriorityExcept(configs, activeAdmins[0].id);
  }

  return configs;
};

/**
 * Handle disabling high priority: ensure another active admin retains priority
 * @param {Array<Object>} configs - Current configurations
 * @param {number} configId - Config id that has high priority disabled
 * @param {boolean} isRoundRobinEnabled - Round Robin mode status
 * @param {Function} getActiveAdmins - Helper to get active admins
 * @param {Function} messageApi - Message notification API
 * @param {Object} messages - Message constants
 * @returns {Array<Object>|null} Updated configs or null to revert
 */
export const handleHighPriorityDisabled = (
  configs,
  configId,
  isRoundRobinEnabled,
  getActiveAdmins,
  messageApi,
  messages
) => {
  if (isRoundRobinEnabled) return configs;

  const activeAdmins = getActiveAdmins(configs);
  const targetAdmin = configs.find((c) => c.id === configId);

  // If this admin is the only active admin, prevent removing high priority
  if (activeAdmins.length === 1 && targetAdmin?.isActive) {
    messageApi?.warning?.(
      "The only active admin must always have high priority. Please activate another admin first.",
      messages.WARNING || 3
    );
    return null; // Signal revert
  }

  // If there are other active admins, ensure one has priority
  if (activeAdmins.length > 1) {
    const otherActiveAdmins = activeAdmins.filter((admin) => admin.id !== configId);
    const adminWithExistingPriority = otherActiveAdmins.find((admin) => admin.isHighPriority);

    // If no other active admin has priority, assign to first one
    if (!adminWithExistingPriority && otherActiveAdmins.length > 0) {
      return resetHighPriorityExcept(
        configs.map((config) =>
          config.id === configId ? { ...config, isHighPriority: false } : config
        ),
        otherActiveAdmins[0].id
      );
    }
  }

  return configs;
};
